import { boolean, string } from "yup"
import { get } from "lodash"

import { Select, Toggle } from "@4cplatform/elements/Forms"
import { List, P } from "@4cplatform/elements/Typography"
import { SelectWrapper } from "../ClaimsHistory/claimsHistory.styles"
import AxaQuestionsTable from "./applicants.axaQuestions.table"

const vitalityQuestionsOptions = [
  {
    order: 0,
    label: "Treatment is expected, anticipated or planned",
    value: "Treatment is expected, anticipated or planned"
  },
  { order: 1, label: "Within the last year", value: "Within the last year" },
  {
    order: 2,
    label: "Between one year and three years ago",
    value: "Between one year and three years ago"
  },
  {
    order: 3,
    label: "Not in the last three years",
    value: "Not in the last three years"
  }
]

export const config = data => {
  const axaSection = get(data, "page.conditionals.has_access_to_axa_agency_codes", false)
    ? [
        {
          key: "axa_questions",
          title: "AXA Questions",
          titleHelperTitle: "AXA Coverage Details",
          titleHelper:
            "<p>If you choose the moratorium option, there are some other specified conditions that we will not cover (as listed below).</p><p>If you have diabetes, treatment of the specified conditions below are also not covered:</p><ul><li>Diabetes</li><li>Coronary heart disease (ischaemic heart disease)</li><li>Cataracts</li><li>Disease to the retina in the eye caused by diabetes (diabetic retinopathy)</li><li>Disease in the kidneys caused by diabetes (diabetic renal disease)</li><li>Arterial disease</li><li>Stroke</li></ul><p>If you are having treatment for high blood pressure, treatment of the specified conditions below are also not covered:</p><ul><li>Raised blood pressure (hypertension)</li><li>Coronary heart disease (ischaemic heart disease)</li><li>Stroke</li><li>Kidney failure caused by high blood pressure (hypertensive renal failure)</li></ul><p>If you are under investigation, having treatment or are undergoing monitoring as a result of a Prostate Specific Antigen (PSA) test, we will not cover treatment of:</p><ul><li>Any prostate conditions</li></ul>",
          components: [
            {
              key: "preamble_1",
              component: P,
              componentProps: {
                children:
                  "In the last five years have you or any other applicants had or received treatment for:"
              },
              skipDataMap: true
            },
            {
              key: "axa_questions_table",
              component: AxaQuestionsTable,
              skipDataMap: true
            }
          ]
        }
      ]
    : []

  const vitalitySection = get(data, "page.conditionals.has_access_to_vitality_agency_codes", true)
    ? [
        {
          key: "vitality_question",
          title: "Vitality Question",
          titleHelper:
            "<p>Answering ‘No’ to this declaration means that our Moratorium+ discount will be applied to the plan.</p><p><i>If we learn that any person insured has falsely answered ‘No’ to this declaration, we will treat this as a medical non-disclosure. In this instance, we reserve the right to remove the Moratorium+ discount. We will then apply our standard Moratorium terms to the plan. The Moratorium+ discount will be removed from the plan start date. Eligible claims may be paused until any additional premiums are paid because of the non-disclosure.</i></p><p><strong>You do not need to tell us about the following:</strong></p><ul><li>Acid Reflux managed at primary care level</li><li>Acne</li><li>All Allergies - unless still being investigated/undergoing tests or under the care of a specialist/consultant</li><li>Anaemia – B12/Pernicious</li><li>Asthma managed at primary care level only with no hospitalisation in the last 5 years</li><li>BPH (benign prostatic hypertrophy) or other post-treatment benign prostate disease which has been stable for at least 18 months</li><li>Conjunctivitis</li><li>Dermatitis</li><li>Ear Infections</li><li>Eczema</li><li>Gout</li><li>Grommets</li><li>Haemorrhoids/Piles managed at primary care level with medication only</li><li>Hayfever</li><li>Hiatus Hernia managed at primary care level with medication only</li><li>High blood pressure and/or high cholesterol that has never required you to see anyone other than your GP</li><li>Indigestion managed at primary care level and not under investigation</li><li>Minor illnesses such as colds and flu which fully resolve within three weeks or less from their beginning</li><li>Oral contraception and HRT</li><li>Physiotherapy more than 6 months ago, where the condition you required physiotherapy for has fully resolved</li><li>Prescribed or over the counter medication where you took it for less than three weeks and the condition is fully resolved</li><li>Sinusitis</li><li>Stable Type II diabetes without complications which has been managed exclusively through routine check-ups with a nurse at your GP practice with either diet or no more than one tablet and has not required inpatient or outpatient care</li><li>Underactive thyroid</li><li>Urinary tract infections or cystitis which has been managed at primary care level only and is not under investigation</li><li>Advice relating to routine dental or optical maintenance</li><li>Treatment that has already taken place relating to the following:<ul><li>gall bladder removal (if due to gallstones)</li><li>hysterectomy (provided not related to any cancer)</li><li>Tonsillectomy or adenoidectomy</li><li>appendectomy</li><li>wisdom teeth removal</li><li>fractures (provided no pins or plates are in place) where you are experiencing no symptoms and have been discharged from follow-up six months ago or greater</li><li>normal and/or natural childbirth where the stay is no longer than 5 days</li></ul><li>For back/spinal conditions, including back pain, and symptoms of sciatica such as leg/buttock pain and Arthritis, joint pain or injury, where there has been symptoms which have lasted for less than one week and do not result in any limitation of activity</li><li>A positive Covid-19 test providing no symptoms were experienced OR symptoms were experienced for less than 12 continuous weeks and are now fully resolved</li></ul>",
          components: [
            {
              key: "last_three_years_injured",
              initialValue: get(data, "page.data.last_three_years_injured", false),
              validationSchema: boolean().required("MISSING_REQUIRED_QUESTION_FIELD"),
              label:
                "In the last three years, has your client or any other person to be insured on this plan:",
              auditLabel:
                "In the last three years, has your client or any other person to be insured on this plan",
              component: Toggle,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "auto",
                isHorizontal: true,
                isRequired: true
              }
            },
            {
              key: "preamble_4",
              component: List,
              componentProps: {
                listType: "unordered",
                name: "information_text",
                children: (
                  <>
                    <li>Experienced symptoms;</li>
                    <li>Received any advice from a healthcare professional;</li>
                    <li>Received treatment or have treatment planned or expected; </li>
                  </>
                )
              },
              skipDataMap: true
            },
            {
              key: "preamble_3",
              component: P,
              componentProps: {
                children: "for any physical, mental health, psychiatric or behavioural problems?"
              },
              skipDataMap: true
            }
          ]
        },
        {
          key: "vitality_additional_questions",
          title: "Vitality Additional Questions",
          condition: {
            type: "formik",
            fieldKey: "last_three_years_injured",
            fieldValue: true
          },
          components: [
            {
              key: "preamble",
              component: P,
              componentProps: {
                children:
                  "When did you or any other person to be insured last experience symptoms, or receive advice from a healthcare professional or receive treatment relating to the following 9 conditions:"
              },
              skipDataMap: true
            },
            {
              key: "last_three_years_cancer",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_cancer", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Cancer",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_heart_conditions",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_heart_conditions", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Heart Conditions",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_prostate",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_prostate", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Prostate",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_diabetes",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_diabetes", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Diabetes",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_mental_health_issues",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_mental_health_issues", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Mental Health Issues",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_back_pain",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_back_pain", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Back Pain",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_arthritis",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_arthritis", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Arthritis",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_admission",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_admission", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label:
                "Admission to a hospital, clinic or other healthcare facility, whether NHS or private, for two nights or more for any condition not listed above",
              component: selectProps => (
                <SelectWrapper>
                  <Select {...selectProps} />
                </SelectWrapper>
              ),
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            },
            {
              key: "last_three_years_other",
              initialValue: get(data, "page.data.last_three_years_injured", false)
                ? get(data, "page.data.last_three_years_other", "Other")
                : "",
              validationSchema: string().when("last_three_years_injured", {
                is: true,
                then: schema => schema.required("MISSING_REQUIRED_QUESTION_FIELD"),
                otherwise: schema => schema.notRequired().nullable()
              }),
              label: "Any other condition not declared above",
              component: Select,
              componentProps: {
                errorPrefix: "none",
                labelWidth: "40rem",
                margin: "0 0 2rem",
                isHorizontal: true,
                options: vitalityQuestionsOptions,
                isRequired: true
              }
            }
          ]
        }
      ]
    : []

  return {
    title: "Medical history",
    cleanBodyOnSubmit: true,
    subtitle: " An opportunity to discuss and make notes on the client's previous medical history",
    sections: [...axaSection, ...vitalitySection]
  }
}
